import { cn } from '@/lib/utils';
import type { WorkflowScriptListItem } from '@/types/workflow';
import { bandLabel, buildSlateHeatmap, type SlateBand } from '@/utils/slateDistribution';

type SlateDistributionHeatmapProps = {
  scripts: WorkflowScriptListItem[];
  className?: string;
};

const BAND_COLOR: Record<SlateBand, string> = {
  greenlight: 'bg-emerald-500/80',
  develop: 'bg-sky-500/70',
  consider: 'bg-amber-500/70',
  hold: 'bg-orange-500/70',
  pass: 'bg-red-500/70',
  queued: 'bg-white/15',
  active: 'bg-white/25',
};

export default function SlateDistributionHeatmap({ scripts, className }: SlateDistributionHeatmapProps) {
  const { formats, bands, matrix, max } = buildSlateHeatmap(scripts);

  if (scripts.length === 0) {
    return (
      <div className={cn('ni-panel p-5', className)}>
        <p className="ni-label mb-2">Slate distribution</p>
        <p className="text-sm text-white/40 font-sans">Ingest screenplays to populate recommendation heatmap.</p>
      </div>
    );
  }

  return (
    <div className={cn('ni-panel p-5', className)}>
      <div className="flex items-center justify-between mb-4">
        <p className="ni-label">Slate distribution</p>
        <span className="text-[10px] font-mono text-white/30">{scripts.length} SCRIPTS</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-[10px] font-mono">
          <thead>
            <tr>
              <th className="text-left text-white/35 pb-2 pr-2">Format</th>
              {bands.map((band) => (
                <th key={band} className="text-center text-white/35 pb-2 px-1 uppercase">
                  {bandLabel(band).slice(0, 3)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formats.map((format) => (
              <tr key={format}>
                <td className="text-white/55 py-1.5 pr-2">{format}</td>
                {bands.map((band) => {
                  const count = matrix[format]?.[band] ?? 0;
                  const intensity = max > 0 ? count / max : 0;
                  return (
                    <td key={band} className="p-0.5">
                      <div
                        title={`${format} · ${bandLabel(band)}: ${count}`}
                        className={cn(
                          'h-7 min-w-[2rem] flex items-center justify-center rounded-sm text-white/80',
                          count > 0 ? BAND_COLOR[band] : 'bg-[#1a1a1a]',
                        )}
                        style={count > 0 && max > 1 ? { opacity: 0.35 + intensity * 0.65 } : undefined}
                      >
                        {count > 0 ? count : ''}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 mt-4 text-[9px] font-mono uppercase text-white/30">
        {bands.map((band) => (
          <span key={band} className="flex items-center gap-1">
            <span className={cn('h-2 w-2 rounded-sm', BAND_COLOR[band])} />
            {bandLabel(band)}
          </span>
        ))}
      </div>
    </div>
  );
}
